import { useState, useEffect, useCallback } from 'react';
import { getCachedData, setCachedData, CACHE_KEYS } from '../utils/cacheUtils';

/**
 * Custom hook to find and load similar electorates
 * 
 * @param {Object} selectedElectorate - The selected electorate to find similar ones for
 * @param {Array} allElectorates - Array of all electorates
 * @param {Object} similarityData - Pre-defined similarity data if available
 * @param {Object} options - Additional options
 * @returns {Object} - { similarElectorates, detailedData, loading, error, refresh }
 */
const useSimilarElectorates = (
  selectedElectorate,
  allElectorates = [],
  similarityData = {},
  options = {}
) => {
  const {
    baseUrl = '',
    limit = 5,
    timeout = 5000,
    useClassification = true,
    useStateFilter = true
  } = options;
  
  // Initialize state with safe defaults
  const [similarElectorates, setSimilarElectorates] = useState([]);
  const [detailedData, setDetailedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Calculate similarity score between two electorates
  const calculateSimilarityScore = useCallback((a, b) => {
    if (!a || !b) return 1; // Max dissimilarity if invalid data
    
    // Normalize values between 0-1 where lower is more similar
    const areaDiff = Math.abs((a.area || 0) - (b.area || 0)) / Math.max((a.area || 1), (b.area || 1));
    const popDiff = a.population && b.population 
      ? Math.abs(a.population - b.population) / Math.max(a.population, b.population)
      : 1;
    const fundingDiff = Math.abs((a.totalValue || 0) - (b.totalValue || 0)) / Math.max((a.totalValue || 1), (b.totalValue || 1));
    
    // Weight factors based on importance (can be adjusted)
    const areaWeight = 0.3;
    const popWeight = 0.4;
    const fundingWeight = 0.3;
    
    // Calculate weighted score
    return (areaDiff * areaWeight) + (popDiff * popWeight) + (fundingDiff * fundingWeight);
  }, []);
  
  // Find similar electorates based on various criteria
  const findSimilarElectorates = useCallback(() => {
    // Make sure we have valid inputs
    if (!selectedElectorate || !Array.isArray(allElectorates) || allElectorates.length === 0) {
      console.log('findSimilarElectorates: missing required data', {
        hasSelectedElectorate: !!selectedElectorate,
        allElectoratesCount: Array.isArray(allElectorates) ? allElectorates.length : 'not an array'
      });
      return [];
    }
    
    // First check for predefined similar electorates
    if (similarityData && similarityData[selectedElectorate.name]) {
      const similarNames = similarityData[selectedElectorate.name];
      
      // Make sure similarNames is an array
      if (Array.isArray(similarNames) && similarNames.length > 0) {
        // Get electorate objects from names
        const predefinedSimilar = similarNames
          .map(name => allElectorates.find(e => e.name === name))
          .filter(Boolean) // Remove any not found
          .slice(0, limit);
        
        if (predefinedSimilar.length > 0) {
          return predefinedSimilar;
        }
      }
    }
    
    try {
      // Initial filter criteria
      let candidates = allElectorates.filter(e => 
        e && e.name && e.name !== selectedElectorate.name &&
        e.area && selectedElectorate.area // Must have area data
      );
      
      // Apply state filter if enabled and there are enough candidates
      if (useStateFilter && selectedElectorate.state) {
        const stateFiltered = candidates.filter(e => e.state === selectedElectorate.state);
        if (stateFiltered.length >= 3) {
          candidates = stateFiltered;
        }
      }
      
      // Apply classification filter if enabled and there are enough candidates
      if (useClassification && selectedElectorate.classification) {
        const classFiltered = candidates.filter(e => 
          e.classification === selectedElectorate.classification
        );
        if (classFiltered.length >= 3) {
          candidates = classFiltered;
        }
      }
      
      // Make sure we still have candidates
      if (candidates.length === 0) {
        console.log('No candidates after filtering, using all electorates');
        candidates = allElectorates.filter(e => 
          e && e.name && e.name !== selectedElectorate.name
        );
      }
      
      // Calculate similarity scores
      const withScores = candidates.map(e => ({
        ...e,
        similarityScore: calculateSimilarityScore(selectedElectorate, e)
      }));
      
      // Sort by similarity and take the top N
      return withScores
        .sort((a, b) => a.similarityScore - b.similarityScore)
        .slice(0, limit);
    } catch (err) {
      console.error('Error in findSimilarElectorates:', err);
      return [];  // Return empty array on error
    }
  }, [selectedElectorate, allElectorates, similarityData, limit, useStateFilter, useClassification, calculateSimilarityScore]);
  
  // Fetch detailed data for a list of electorates
  const fetchDetailedData = useCallback(async (electorates) => {
    if (!electorates || !Array.isArray(electorates) || electorates.length === 0) return [];
    
    try {
      const promises = electorates.map(async (electorate) => {
        if (!electorate || !electorate.name) return null;
        
        // Check cache first
        const cacheKey = `${CACHE_KEYS.ELECTORATE_DATA}${electorate.name}`;
        const cachedData = getCachedData(cacheKey);
        
        if (cachedData) {
          return cachedData;
        }
        
        try {
          const fileName = electorate.name.replace(/\s+/g, '_');
          const url = `${baseUrl}/data/electorates/${fileName}.json`;
          
          // Set up request with timeout
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), timeout);
          
          try {
            const response = await fetch(url, { 
              signal: controller.signal,
              headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache'
              }
            });
            clearTimeout(timeoutId);
            
            if (!response.ok) {
              console.warn(`Failed to load data for ${electorate.name}: ${response.status}`);
              return null;
            }
            
            const data = await response.json();
            
            // Cache the fetched data
            setCachedData(cacheKey, data);
            
            return data;
          } catch (err) {
            clearTimeout(timeoutId);
            if (err.name === 'AbortError') {
              console.warn(`Request timed out for ${electorate.name}`);
            } else {
              console.error(`Error loading data for ${electorate.name}:`, err);
            }
            return null;
          }
        } catch (error) {
          console.error(`Error processing ${electorate.name}:`, error);
          return null;
        }
      });
      
      // Wait for all promises to resolve
      const results = await Promise.all(promises);
      return results.filter(Boolean); // Remove null results
    } catch (err) {
      console.error('Error in fetchDetailedData:', err);
      return [];  // Return empty array on error
    }
  }, [baseUrl, timeout]);
  
  // Effect to find similar electorates when selected electorate changes
  useEffect(() => {
    const loadSimilarElectorates = async () => {
      if (!selectedElectorate) {
        setSimilarElectorates([]);
        setDetailedData([]);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Find similar electorates
        const similar = findSimilarElectorates();
        setSimilarElectorates(similar || []); // Ensure we always set an array
        
        // Load detailed data if we have similar electorates
        if (similar && similar.length > 0) {
          const detailed = await fetchDetailedData(similar);
          setDetailedData(detailed || []); // Ensure we always set an array
        } else {
          setDetailedData([]);
        }
        
      } catch (err) {
        console.error('Error finding similar electorates:', err);
        setError(err.message || 'Error finding similar electorates');
        // Reset states to safe defaults on error
        setSimilarElectorates([]);
        setDetailedData([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadSimilarElectorates();
  }, [selectedElectorate, findSimilarElectorates, fetchDetailedData]);
  
  // Function to force refresh the data
  const refresh = useCallback(async () => {
    if (!selectedElectorate) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Find similar electorates
      const similar = findSimilarElectorates();
      setSimilarElectorates(similar || []); // Ensure we always set an array
      
      // Force fetch data for all similar electorates without cache
      if (similar && similar.length > 0) {
        const promises = similar.map(async (electorate) => {
          if (!electorate || !electorate.name) return null;
          
          try {
            const fileName = electorate.name.replace(/\s+/g, '_');
            const url = `${baseUrl}/data/electorates/${fileName}.json`;
            
            const response = await fetch(url, {
              headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
                'Expires': '0'
              }
            });
            
            if (!response.ok) {
              console.warn(`Failed to load data for ${electorate.name}: ${response.status}`);
              return null;
            }
            
            const data = await response.json();
            
            // Update cache
            const cacheKey = `${CACHE_KEYS.ELECTORATE_DATA}${electorate.name}`;
            setCachedData(cacheKey, data);
            
            return data;
          } catch (error) {
            console.error(`Error processing ${electorate.name}:`, error);
            return null;
          }
        });
        
        // Wait for all promises to resolve
        const results = await Promise.all(promises);
        const validResults = results.filter(Boolean);
        setDetailedData(validResults || []); // Ensure we always set an array
      } else {
        setDetailedData([]);
      }
    } catch (err) {
      console.error('Error refreshing similar electorates:', err);
      setError(err.message || 'Error refreshing similar electorates');
      // Reset states to safe defaults on error
      setSimilarElectorates([]);
      setDetailedData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedElectorate, baseUrl, findSimilarElectorates]);
  
  return {
    similarElectorates: similarElectorates || [],
    detailedData: detailedData || [],
    loading,
    error,
    refresh
  };
};

export default useSimilarElectorates; 